/**
 * Thin mutation client for feedback reactions.
 * POST /api/feedback -- append-only.
 */

import { supabase } from './supabase'
import { fetchWithTimeout } from './http'
import type { FeedbackRating, FeedbackTarget } from '../types/feedback'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export class FeedbackSubmitError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'FeedbackSubmitError'
  }
}

interface SubmitArgs extends FeedbackTarget {
  rating: FeedbackRating | 'cleared'
  comment: string | null
}

export interface SubmitResponse {
  id: string
  session_id: string
  message_id: string | null
  rating: FeedbackRating | 'cleared'
  comment: string | null
  created_at: string
  is_session_level: boolean
  is_cleared: boolean
}

async function authHeader(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  try {
    if (supabase) {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`
      }
    }
  } catch {
    // Auth unavailable; proceed without token (dev mode).
  }
  return headers
}

export async function submitReaction(args: SubmitArgs): Promise<SubmitResponse> {
  const headers = await authHeader()
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/feedback`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      session_id: args.sessionId,
      message_id: args.messageId,
      rating: args.rating,
      comment: args.comment,
    }),
  })

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const body = (await response.json()) as { detail?: string }
      if (body?.detail) detail = body.detail
    } catch {
      // Response body was not JSON — fall back to status-code detail.
    }
    throw new FeedbackSubmitError(response.status, detail)
  }

  return (await response.json()) as SubmitResponse
}

export async function clearReaction(target: FeedbackTarget): Promise<SubmitResponse> {
  return submitReaction({ ...target, rating: 'cleared', comment: null })
}

/**
 * Returned by createReactionHandler — wraps submit/clear with optimistic dispatch.
 * The dispatch closure is supplied by useChatHistory so this helper stays pure.
 */
export interface ReactionHandler {
  toggle: (rating: FeedbackRating) => Promise<void>
  setComment: (comment: string) => Promise<void>
}

export interface ReactionDispatch {
  /** Write optimistic state immediately. Returns the client-assigned updated_at. */
  optimistic: (rating: FeedbackRating | 'cleared', comment: string | null) => string
  /** Commit server state (called on success). */
  commit: (updatedAt: string, rating: FeedbackRating | 'cleared', comment: string | null, serverUpdatedAt: string) => void
  /** Rollback on failure. */
  rollback: (updatedAt: string) => void
  /** Current rating (for toggle semantics). */
  currentRating: () => FeedbackRating | null
  /** Current comment (for comment-only updates). */
  currentComment: () => string | null
}

export function createReactionHandler(target: FeedbackTarget, dispatch: ReactionDispatch): ReactionHandler {
  async function submit(rating: FeedbackRating | 'cleared', comment: string | null): Promise<void> {
    const ts = dispatch.optimistic(rating, comment)
    try {
      const res = await submitReaction({ ...target, rating, comment })
      dispatch.commit(ts, rating, comment, res.created_at)
    } catch (err) {
      dispatch.rollback(ts)
      throw err
    }
  }

  return {
    async toggle(rating: FeedbackRating) {
      const current = dispatch.currentRating()
      if (current === rating) {
        await submit('cleared', null)
      } else {
        await submit(rating, dispatch.currentComment())
      }
    },
    async setComment(comment: string) {
      const current = dispatch.currentRating()
      if (current === null) return // can't attach a comment without a rating
      await submit(current, comment.trim() || null)
    },
  }
}
